import { useState, useEffect } from 'react';
import './ICHarmony.css';
import { supabase } from '../../supabase';
import { Button } from '../../components/ui/button';
import { Calendar, Download, ChevronDown, Music, Heart, Users, Target, Clock, Trophy, CheckCircle, MessageCircle, Check, ChevronRight, User, ShoppingBag, ChevronLeft, Mic2, UserCheck, Volume2, BookOpen, Award, Shirt, Sparkles, HelpCircle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import MetaTags from '../../components/layout/MetaTags';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import WhatsAppFloat from '../../components/WhatsAppFloat';

// Import IC Harmony logo
import icHarmonyLogo from '../../assets/images/logo-event/ic-harmony-logo.png';

// ==================== HERO SECTION ====================
function Hero() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background - Cream/Gold gradient to complement green/gold logo */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#f8f4e8] via-[#f5ecd7] to-[#e8d5a3]">
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#1a4d2e]/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#c9a227]/20 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#1a4d2e]/5 rounded-full blur-3xl" />
        </div>
        {/* Islamic Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30L30 0z' fill='none' stroke='%231a4d2e' stroke-width='1'/%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-20 text-center">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 bg-[#c9a227]/20 rounded-full blur-2xl" />
            <img 
              src={icHarmonyLogo}
              alt="IC Harmony 2026" 
              className="relative w-32 h-32 md:w-48 md:h-48 object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-[#1a4d2e] mb-4 tracking-tight drop-shadow-sm">
          IC <span className="text-[#c9a227]">HARMONY</span> 2026
        </h1>
        
        {/* Tagline */}
        <p className="text-xl md:text-2xl text-[#c9a227] mb-2 font-bold">
          "Merajut Harmoni dalam Irama"
        </p>
        <p className="text-xl md:text-2xl text-[#2d6a4f] mb-6 font-medium">
          Festival Lomba Hadroh & Nasyid
        </p>

        {/* Date Badge */}
        <div className="inline-flex items-center gap-2 bg-[#1a4d2e]/10 backdrop-blur-sm border border-[#c9a227]/50 rounded-full px-6 py-3 mb-8">
          <Calendar className="w-5 h-5 text-[#c9a227]" />
          <span className="text-[#1a4d2e] font-semibold">16 - 18 April 2026</span>
        </div>

        {/* Description */}
        <p className="max-w-2xl mx-auto text-[#1a4d2e]/80 text-lg mb-10 leading-relaxed">
          Rayakan keindahan seni islami bersama kami. 
          Tunjukkan talenta terbaik grup Hadroh dan Nasyid Anda dalam festival kompetisi terbesar di Bangka Belitung.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button 
            size="lg"
            onClick={() => scrollToSection('daftar')}
            className="bg-[#c9a227] hover:bg-[#b8941f] text-[#1a4d2e] font-bold px-8 py-6 text-lg rounded-full shadow-lg shadow-[#c9a227]/30 transition-all hover:scale-105"
          >
            Daftar Sekarang
          </Button>
          <Button 
            size="lg"
            variant="outline"
            onClick={() => window.open('/documents/PETUNJUK-TEKNIS-IC-HARMONY-2026.pdf', '_blank')}
            className="border-2 border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white font-semibold px-8 py-6 text-lg rounded-full bg-white/50 backdrop-blur-sm transition-all"
          >
            <Download className="w-5 h-5 mr-2" />
            Unduh Juknis
          </Button>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <button 
            onClick={() => scrollToSection('tentang')}
            className="text-[#1a4d2e]/60 hover:text-[#1a4d2e] transition-colors"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ==================== ABOUT SECTION ====================
const aboutFeatures = [
  {
    icon: Music,
    title: 'Festival Hadroh',
    description: 'Kompetisi grup Hadroh dengan lagu wajib Ajmala Zikro dan lagu pilihan.'
  },
  {
    icon: Heart,
    title: 'Festival Nasyid',
    description: 'Kompetisi grup Nasyid dengan berbagai genre: Acapella, Akustik, dan Minus One.'
  },
  {
    icon: Users,
    title: 'Untuk Semua',
    description: 'Terbuka untuk pelajar SMP/MTs, SMA/MA, Pondok Pesantren, Perguruan Tinggi, dan Umum.'
  },
  {
    icon: Target,
    title: 'Tujuan Mulia',
    description: 'Melestarikan kesenian Islami dan memperkokoh ukhuwah Islamiyyah.'
  }
];

function About() {
  return (
    <section id="tentang" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#c9a227] font-semibold text-sm uppercase tracking-wider mb-3">
            Tentang Event
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a4d2e] mb-6">
            Apa itu IC Harmony 2026?
          </h2>
          <div className="w-24 h-1 bg-[#c9a227] mx-auto rounded-full" />
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong className="text-[#1a4d2e]">IC Harmony 2026</strong> adalah festival kompetisi seni Islami 
              yang diselenggarakan oleh Bahrul Ulum IC. Event ini menjadi wadah bagi para penyuka seni Hadroh 
              dan Nasyid untuk menunjukkan bakat dan kreativitas mereka.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Dengan mengusung tema keharmonisan dan ukhuwah Islamiyyah, IC Harmony 2026 bertujuan untuk 
              mengembangkan dan melestarikan kesenian budaya Islam, menambah rasa kecintaan kepada Allah SWT 
              dan Rasul-Nya, serta merajut kebersamaan dalam keberagaman.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="bg-[#1a4d2e]/5 rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#1a4d2e]">2</p>
                <p className="text-sm text-gray-600">Kategori Lomba</p>
              </div>
              <div className="bg-[#1a4d2e]/5 rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#1a4d2e]">3</p>
                <p className="text-sm text-gray-600">Hari Pelaksanaan</p>
              </div>
              <div className="bg-[#1a4d2e]/5 rounded-xl px-6 py-4">
                <p className="text-3xl font-bold text-[#1a4d2e]">30</p>
                <p className="text-sm text-gray-600">Max Usia (Tahun)</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a4d2e] to-[#2d6a4f] rounded-3xl transform rotate-3" />
            <div className="relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
              <img 
                src={icHarmonyLogo}
                alt="IC Harmony Logo" 
                className="w-full max-w-xs mx-auto mb-6"
              />
              <div className="text-center">
                <p className="text-[#1a4d2e] font-bold text-xl">IC HARMONY 2026</p>
                <p className="text-[#c9a227] font-medium">Festival Hadroh & Nasyid</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {aboutFeatures.map((feature, index) => (
            <div 
              key={index}
              className="group bg-gradient-to-br from-[#1a4d2e]/5 to-transparent rounded-2xl p-6 hover:shadow-lg transition-all duration-300 border border-[#1a4d2e]/10 hover:border-[#c9a227]/30"
            >
              <div className="w-14 h-14 bg-[#1a4d2e] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#c9a227] transition-colors">
                <feature.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-bold text-[#1a4d2e] mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ==================== TIMELINE SECTION ====================
const timelineEvents = [
  {
    date: '16 Feb - 10 Apr 2026',
    title: 'Pendaftaran',
    description: 'Periode pendaftaran peserta lomba Hadroh dan Nasyid',
    icon: Calendar,
    status: 'upcoming'
  },
  {
    date: '11 April 2026',
    title: 'Technical Meeting',
    description: 'Pertemuan teknis dan pengambilan nomor urut peserta',
    icon: Users,
    status: 'upcoming',
    time: '10.00 WIB'
  },
  {
    date: '16 - 17 April 2026',
    title: 'Pelaksanaan Lomba Hadroh',
    description: 'Kompetisi Festival Hadroh selama 2 hari',
    icon: Clock,
    status: 'upcoming',
    time: '09.00 - Selesai'
  },
  {
    date: '18 April 2026',
    title: 'Pelaksanaan Lomba Nasyid',
    description: 'Kompetisi Festival Nasyid',
    icon: Clock,
    status: 'upcoming',
    time: '08.00 - 15.00 WIB'
  },
  {
    date: '18 April 2026',
    title: 'Pengumuman & Pembagian Hadiah',
    description: 'Pengumuman pemenang dan penyerahan hadiah',
    icon: Trophy,
    status: 'upcoming',
    time: '16.00 - Selesai'
  }
];

function Timeline() {
  return (
    <section id="timeline" className="py-20 md:py-32 bg-gradient-to-b from-[#f8faf8] to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#c9a227] font-semibold text-sm uppercase tracking-wider mb-3">
            Jadwal Kegiatan
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a4d2e] mb-6">
            Timeline Event
          </h2>
          <div className="w-24 h-1 bg-[#c9a227] mx-auto rounded-full mb-6" />
          <p className="max-w-2xl mx-auto text-gray-600">
            Ikuti setiap tahapan kegiatan IC Harmony 2026. Jangan sampai ketinggalan!
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#1a4d2e] via-[#c9a227] to-[#1a4d2e] md:-translate-x-1/2" />

            {/* Timeline Items */}
            <div className="space-y-12">
              {timelineEvents.map((event, index) => (
                <div 
                  key={index}
                  className={`relative flex items-start gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Icon Circle */}
                  <div className="absolute left-8 md:left-1/2 w-16 h-16 bg-white rounded-full border-4 border-[#1a4d2e] flex items-center justify-center z-10 md:-translate-x-1/2 shadow-lg">
                    <event.icon className="w-7 h-7 text-[#c9a227]" />
                  </div>

                  {/* Content Card */}
                  <div className={`ml-24 md:ml-0 md:w-5/12 ${
                    index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'
                  }`}>
                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                      <span className="inline-block bg-[#1a4d2e] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                        {event.date}
                      </span>
                      <h3 className="text-xl font-bold text-[#1a4d2e] mb-2">
                        {event.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-2">
                        {event.description}
                      </p>
                      {event.time && (
                        <p className="text-[#c9a227] font-semibold text-sm">
                          Pukul: {event.time}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Empty space for alternating layout */}
                  <div className="hidden md:block md:w-5/12" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-4">
            Jangan lewatkan kesempatan untuk menjadi bagian dari event spektakuler ini!
          </p>
          <button 
            onClick={() => document.getElementById('daftar')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 bg-[#1a4d2e] text-white font-semibold px-8 py-3 rounded-full hover:bg-[#2d6a4f] transition-colors"
          >
            Daftar Sekarang
          </button>
        </div>
      </div>
    </section>
  );
}

// ==================== CATEGORIES SECTION ====================
const categories = [
  {
    id: 'hadroh',
    title: 'Festival Lomba Hadroh',
    icon: Music,
    color: 'from-[#1a4d2e] to-[#2d6a4f]',
    requirements: [
      { icon: UserCheck, text: 'Peserta: SMP – Umum (usia maks. 30 tahun)' },
      { icon: Users, text: 'Jumlah anggota: 10 – 15 orang' },
      { icon: Clock, text: 'Durasi tampil: maks. 12 menit (2 lagu)' },
      { icon: Volume2, text: '1 Lagu Wajib + 1 Lagu Pilihan' }
    ],
    songs: {
      wajib: ['Ajmala Zikro'],
      pilihan: ['Qod Anshoha', 'Shollu \'ala Nuril Ladzi', 'Rabbi Ya Rabb']
    },
    criteria: [
      { name: 'Teknik Vokal & Fashohah', bobot: '50%' },
      { name: 'Instrumen', bobot: '35%' },
      { name: 'Penampilan/Performance', bobot: '15%' }
    ]
  },
  {
    id: 'nasyid',
    title: 'Festival Lomba Nasyid',
    icon: Mic2,
    color: 'from-[#c9a227] to-[#d4af37]',
    requirements: [
      { icon: UserCheck, text: 'Peserta: SMP – Umum (usia maks. 30 tahun)' },
      { icon: Users, text: 'Jumlah anggota: 3 – 6 orang' },
      { icon: Clock, text: 'Durasi tampil: maks. 10 menit (2 lagu)' },
      { icon: Volume2, text: 'Genre: Acapella, Akustik, Minus One' }
    ],
    songs: {
      wajib: ['Rumus Canggih (Justice Voice)', 'Muhasabah Cinta (Edcoustic)', 'Kun Anta (Humood)', 'Qalbi Fil Madinah'],
      pilihan: ['1 Lagu Bebas']
    },
    criteria: [
      { name: 'Materi Suara', bobot: '35%' },
      { name: 'Teknik Vokal', bobot: '45%' },
      { name: 'Penampilan/Performance', bobot: '20%' }
    ]
  }
];

function Categories() {
  const [activeTab, setActiveTab] = useState('hadroh');
  const activeCategory = categories.find(c => c.id === activeTab);

  return (
    <section id="kategori" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#c9a227] font-semibold text-sm uppercase tracking-wider mb-3">
            Kategori Lomba
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a4d2e] mb-6">
            Pilih Kategori Lomba
          </h2>
          <div className="w-24 h-1 bg-[#c9a227] mx-auto rounded-full mb-6" />
          <p className="max-w-2xl mx-auto text-gray-600">
            IC Harmony 2026 menyelenggarakan 2 kategori lomba dengan ketentuan dan kriteria penilaian yang berbeda
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex justify-center gap-4 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`flex items-center gap-3 px-8 py-4 rounded-full font-semibold transition-all ${
                activeTab === cat.id
                  ? 'bg-[#1a4d2e] text-white shadow-lg shadow-[#1a4d2e]/30'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <cat.icon className="w-5 h-5" />
              {cat.title}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeCategory && (
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left Column - Requirements */}
              <div className="space-y-6">
                <div className={`bg-gradient-to-br ${activeCategory.color} rounded-3xl p-8 text-white`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                      <activeCategory.icon className="w-8 h-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{activeCategory.title}</h3>
                      <p className="text-white/80">Ketentuan Peserta</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {activeCategory.requirements.map((req, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <req.icon className="w-4 h-4" />
                        </div>
                        <p className="text-white/90">{req.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Download Button */}
                <Button 
                  variant="outline"
                  onClick={() => window.open('/PETUNJUK TEKNIS IC HARMONY 2026.pdf', '_blank')}
                  className="w-full border-2 border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white font-semibold py-6 rounded-xl"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Lihat Juknis Lengkap (PDF)
                </Button>
              </div>

              {/* Right Column - Songs & Criteria */}
              <div className="space-y-6">
                {/* Lagu */}
                <div className="bg-[#f8faf8] rounded-3xl p-6 border border-[#1a4d2e]/10">
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="w-6 h-6 text-[#c9a227]" />
                    <h4 className="text-lg font-bold text-[#1a4d2e]">Materi Lagu</h4>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-[#1a4d2e] mb-2">Lagu Wajib:</p>
                      <ul className="space-y-2">
                        {activeCategory.songs.wajib.map((song, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-gray-700">
                            <ChevronRight className="w-4 h-4 text-[#c9a227]" />
                            {song}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {activeCategory.songs.pilihan.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-[#1a4d2e] mb-2">Lagu Pilihan/Bebas:</p>
                        <ul className="space-y-2">
                          {activeCategory.songs.pilihan.map((song, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-gray-700">
                              <ChevronRight className="w-4 h-4 text-[#c9a227]" />
                              {song}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Kriteria Penilaian */}
                <div className="bg-[#f8faf8] rounded-3xl p-6 border border-[#1a4d2e]/10">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="w-6 h-6 text-[#c9a227]" />
                    <h4 className="text-lg font-bold text-[#1a4d2e]">Kriteria Penilaian</h4>
                  </div>
                  
                  <div className="space-y-3">
                    {activeCategory.criteria.map((crit, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white rounded-xl p-4">
                        <span className="text-gray-700">{crit.name}</span>
                        <span className="bg-[#1a4d2e] text-white text-sm font-bold px-3 py-1 rounded-full">
                          {crit.bobot}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ==================== MERCHANDISE SECTION ====================
const products = [
  {
    id: 'kaos',
    name: 'Kaos Event IC Harmony 2026',
    image: '/kaos-mockup.jpg',
    icon: Shirt,
    price: 'Rp 120.000',
    features: [
      'Bahan Cotton Combed 30s Premium',
      'Desain Eksklusif Edisi Event',
      'Sablon Plastisol Berkualitas',
      'Tersedia Ukuran S - XXL'
    ],
    colors: ['Hijau Tua', 'Hitam'],
    badge: 'Best Seller'
  },
  {
    id: 'jersey',
    name: 'Jersey Event IC Harmony 2026',
    image: '/jersey-mockup.jpg',
    icon: Sparkles,
    price: 'Rp 180.000',
    features: [
      'Bahan Dryfit Premium',
      'Desain Sporty Modern',
      'Nyaman dan Adem',
      'Tersedia Ukuran S - XXL'
    ],
    colors: ['Hijau Tua/Gold'],
    badge: 'Limited Edition'
  }
];

function Merchandise() {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const scrollToForm = () => {
    const formSection = document.getElementById('daftar');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="merchandise" className="py-20 md:py-32 bg-gradient-to-br from-[#1a4d2e] via-[#2d6a4f] to-[#1a4d2e] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 0L80 40L40 80L0 40L40 0z' fill='none' stroke='%23c9a227' stroke-width='1'/%3E%3C/svg%3E")`,
            backgroundSize: '80px 80px'
          }}
        />
      </div>
      
      {/* Decorative Circles */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 bg-[#c9a227]/20 text-[#e8d5a3] font-semibold text-sm uppercase tracking-wider px-4 py-2 rounded-full mb-4">
            <ShoppingBag className="w-4 h-4" />
            Merchandise Resmi
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Merchandise Eksklusif
          </h2>
          <div className="w-24 h-1 bg-[#c9a227] mx-auto rounded-full mb-6" />
          <p className="max-w-2xl mx-auto text-white/80">
            Tersedia Kaos & Jersey Eksklusif Edisi IC Harmony 2026. 
            Dapatkan merchandise resmi event dengan desain terbatas!
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {products.map((product) => (
            <div 
              key={product.id}
              className={`group bg-white/10 backdrop-blur-sm rounded-3xl overflow-hidden border transition-all duration-300 ${
                selectedProduct === product.id 
                  ? 'border-[#c9a227] ring-2 ring-[#c9a227]/50' 
                  : 'border-white/20 hover:border-[#c9a227]/50'
              }`}
              onClick={() => setSelectedProduct(product.id)}
            >
              {/* Badge */}
              <div className="absolute top-4 right-4 z-10">
                <span className="bg-[#c9a227] text-[#1a4d2e] text-xs font-bold px-3 py-1 rounded-full">
                  {product.badge}
                </span>
              </div>

              {/* Image */}
              <div className="relative aspect-square overflow-hidden bg-white/5">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a4d2e]/80 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#c9a227]/20 rounded-xl flex items-center justify-center">
                    <product.icon className="w-5 h-5 text-[#c9a227]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{product.name}</h3>
                    <p className="text-[#c9a227] font-bold">{product.price}</p>
                  </div>
                </div>

                <ul className="space-y-2 mb-4">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-white/80 text-sm">
                      <Check className="w-4 h-4 text-[#c9a227] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color, idx) => (
                    <span 
                      key={idx}
                      className="text-xs bg-white/10 text-white/80 px-3 py-1 rounded-full"
                    >
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info & CTA */}
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-8">
            <p className="text-white/90 mb-2">
              <strong className="text-[#c9a227]">Catatan Penting:</strong>
            </p>
            <p className="text-white/70 text-sm">
              Merchandise dapat dipesan saat melakukan pendaftaran melalui form pendaftaran. 
              Stok terbatas, pastikan Anda memesan sebelum kehabisan!
            </p>
          </div>

          <Button 
            size="lg"
            onClick={scrollToForm}
            className="bg-[#c9a227] hover:bg-[#b8941f] text-[#1a4d2e] font-bold px-10 py-6 text-lg rounded-full shadow-lg shadow-[#c9a227]/30 transition-all hover:scale-105"
          >
            <ShoppingBag className="w-5 h-5 mr-2" />
            Pesan Saat Pendaftaran
          </Button>
        </div>
      </div>
    </section>
  );
}

// ==================== REGISTRATION FORM SECTION ====================
// Song options for each category
const songOptions = {
  hadroh: {
    wajib: ['Ajmala Zikro'],
    pilihan: ['Qod Anshoha', "Shollu 'ala Nuril Ladzi", 'Rabbi Ya Rabb']
  },
  nasyid: {
    wajib: ['Rumus Canggih (Justice Voice)', 'Muhasabah Cinta (Edcoustic)', 'Kun Anta (Humood)', 'Qalbi Fil Madinah'],
    pilihan: ['Lagu Bebas (Tuliskan judul lagu di kolom yang tersedia)']
  }
};

const ukuranOptions = ['S', 'M', 'L', 'XL', 'XXL'];

const initialFormData = {
  namaGrup: '',
  jenisLomba: '',
  asalInstansi: '',
  kategoriPeserta: '',
  jumlahAnggota: '',
  namaKetua: '',
  noWhatsapp: '',
  email: '',
  anggota: [],
  // Song selection
  laguWajib: '',
  laguPilihan: '',
  // Merchandise
  pesanJersey: '',
  jerseyDetails: [],
  pesanTopi: false,
  topiJumlah: '',
  setuju: false,
};

function RegistrationForm({ onSubmitSuccess }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState('kaos');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.namaGrup) newErrors.namaGrup = 'Nama grup wajib diisi';
      if (!formData.jenisLomba) newErrors.jenisLomba = 'Jenis lomba wajib dipilih';
      if (!formData.asalInstansi) newErrors.asalInstansi = 'Asal instansi wajib diisi';
      if (!formData.jumlahAnggota) newErrors.jumlahAnggota = 'Jumlah anggota wajib diisi';
    }
    
    if (currentStep === 2) {
      // Song selection validation
      if (!formData.laguWajib) newErrors.laguWajib = 'Lagu wajib wajib dipilih';
      if (!formData.laguPilihan) newErrors.laguPilihan = 'Lagu pilihan wajib diisi';
    }
    
    if (currentStep === 3) {
      if (!formData.namaKetua) newErrors.namaKetua = 'Nama ketua wajib diisi';
      if (!formData.noWhatsapp) newErrors.noWhatsapp = 'Nomor WhatsApp wajib diisi';
      else if (!/^[0-9]{10,13}$/.test(formData.noWhatsapp.replace(/\D/g, ''))) {
        newErrors.noWhatsapp = 'Nomor WhatsApp tidak valid';
      }
    }

    if (currentStep === 4) {
      // No validation needed for merchandise step (optional)
    }

    if (currentStep === 5 && !formData.setuju) {
      newErrors.setuju = 'Anda harus menyetujui pernyataan ini';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      // Skip step 4 (Merch) - go from step 3 directly to step 5
      if (step === 3) {
        setStep(5);
      } else {
        setStep(step + 1);
      }
      setErrors({});
    }
  };

  const handleBack = () => {
    // Skip step 4 (Merch) - go from step 5 back to step 3
    if (step === 5) {
      setStep(3);
    } else {
      setStep(step - 1);
    }
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Calculate total cost
      const basePrice = formData.jenisLomba === 'hadroh' ? 150000 : 75000;
      const jerseyCost = formData.pesanJersey 
        ? formData.jerseyDetails.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) * 115000 
        : 0;
      const topiCost = formData.pesanTopi 
        ? (parseInt(formData.topiJumlah) || 0) * 115000 
        : 0;
      const totalBiaya = basePrice + jerseyCost + topiCost;
      
      // Prepare data for Supabase
      const registrationData = {
        nama_grup: formData.namaGrup,
        jenis_lomba: formData.jenisLomba,
        asal_instansi: formData.asalInstansi,
        jumlah_anggota: parseInt(formData.jumlahAnggota) || 0,
        nama_ketua: formData.namaKetua,
        no_whatsapp: formData.noWhatsapp,
        email: formData.email || null,
        // Song selection
        lagu_wajib: formData.laguWajib,
        lagu_pilihan: formData.laguPilihan,
        // Merchandise
        jersey_design: formData.pesanJersey || null,
        jersey_details: formData.jerseyDetails.length > 0 ? formData.jerseyDetails : [],
        topi_pre_order: formData.pesanTopi,
        topi_jumlah: parseInt(formData.topiJumlah) || 0,
        total_biaya: totalBiaya,
        status: 'pending'
      };
      
      // Insert to Supabase
      const { error } = await supabase
        .from('ic_harmony_registrations')
        .insert([registrationData])
        .select();
        
      if (error) {
        console.error('Supabase error:', error);
        throw new Error(error.message || 'Gagal menyimpan data pendaftaran');
      }
      
      // Success - call parent handler
      onSubmitSuccess(formData);
      
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitError(err.message || 'Terjadi kesalahan saat menyimpan data. Silakan coba lagi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addMerchandiseDetail = (type) => {
    const field = type === 'kaos' ? 'kaosDetails' : 'jerseyDetails';
    const current = formData[field];
    updateFormData(field, [...current, { ukuran: 'M', jumlah: '1' }]);
  };

  const updateMerchandiseDetail = (type, index, field, value) => {
    const fieldName = type === 'kaos' ? 'kaosDetails' : 'jerseyDetails';
    const current = [...formData[fieldName]];
    current[index] = { ...current[index], [field]: value };
    updateFormData(fieldName, current);
  };

  const removeMerchandiseDetail = (type, index) => {
    const fieldName = type === 'kaos' ? 'kaosDetails' : 'jerseyDetails';
    const current = formData[fieldName].filter((_, i) => i !== index);
    updateFormData(fieldName, current);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
              <Users className="w-5 h-5" />
              Data Umum Pendaftar
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="namaGrup">Nama Grup / Tim *</Label>
                <Input
                  id="namaGrup"
                  placeholder="Masukkan nama grup"
                  value={formData.namaGrup}
                  onChange={(e) => updateFormData('namaGrup', e.target.value)}
                  className={errors.namaGrup ? 'border-red-500' : ''}
                />
                {errors.namaGrup && <p className="text-red-500 text-sm">{errors.namaGrup}</p>}
              </div>

              <div className="space-y-2">
                <Label>Jenis Lomba *</Label>
                <Select 
                  value={formData.jenisLomba} 
                  onValueChange={(value) => updateFormData('jenisLomba', value)}
                >
                  <SelectTrigger className={errors.jenisLomba ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Pilih jenis lomba" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hadroh">Festival Lomba Hadroh</SelectItem>
                    <SelectItem value="nasyid">Festival Lomba Nasyid</SelectItem>
                  </SelectContent>
                </Select>
                {errors.jenisLomba && <p className="text-red-500 text-sm">{errors.jenisLomba}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="asalInstansi">Asal Instansi / Sekolah / Komunitas *</Label>
                <Input
                  id="asalInstansi"
                  placeholder="Masukkan asal instansi"
                  value={formData.asalInstansi}
                  onChange={(e) => updateFormData('asalInstansi', e.target.value)}
                  className={errors.asalInstansi ? 'border-red-500' : ''}
                />
                {errors.asalInstansi && <p className="text-red-500 text-sm">{errors.asalInstansi}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="jumlahAnggota">Jumlah Anggota *</Label>
                <Input
                  id="jumlahAnggota"
                  type="number"
                  placeholder="Masukkan jumlah anggota"
                  value={formData.jumlahAnggota}
                  onChange={(e) => updateFormData('jumlahAnggota', e.target.value)}
                  className={errors.jumlahAnggota ? 'border-red-500' : ''}
                  min="1"
                />
                {errors.jumlahAnggota && <p className="text-red-500 text-sm">{errors.jumlahAnggota}</p>}
                <p className="text-gray-500 text-xs">Jumlah anggota tidak dibatasi</p>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
              <Music className="w-5 h-5" />
              Pilihan Lagu
            </h3>
            
            {!formData.jenisLomba ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-yellow-800">
                <p>Silakan pilih jenis lomba terlebih dahulu di langkah sebelumnya.</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-800 mb-4">
                  <p className="font-semibold">Kategori: {formData.jenisLomba === 'hadroh' ? 'Festival Hadroh' : 'Festival Nasyid'}</p>
                  <p className="text-sm mt-1">
                    {formData.jenisLomba === 'hadroh' 
                      ? '1 Lagu Wajib + 1 Lagu Pilihan (Total: 2 lagu, maks. 12 menit)'
                      : '1 Lagu Wajib + 1 Lagu Bebas (Total: 2 lagu, maks. 10 menit)'}
                  </p>
                </div>

                {/* Lagu Wajib */}
                <div className="space-y-2">
                  <Label>Lagu Wajib *</Label>
                  <Select 
                    value={formData.laguWajib} 
                    onValueChange={(value) => updateFormData('laguWajib', value)}
                  >
                    <SelectTrigger className={errors.laguWajib ? 'border-red-500' : ''}>
                      <SelectValue placeholder={`Pilih lagu wajib ${formData.jenisLomba === 'hadroh' ? 'Hadroh' : 'Nasyid'}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {songOptions[formData.jenisLomba]?.wajib.map((song) => (
                        <SelectItem key={song} value={song}>{song}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.laguWajib && <p className="text-red-500 text-sm">{errors.laguWajib}</p>}
                </div>

                {/* Lagu Pilihan */}
                <div className="space-y-2">
                  <Label>Lagu Pilihan *</Label>
                  {formData.jenisLomba === 'hadroh' ? (
                    <Select 
                      value={formData.laguPilihan} 
                      onValueChange={(value) => updateFormData('laguPilihan', value)}
                    >
                      <SelectTrigger className={errors.laguPilihan ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Pilih lagu pilihan" />
                      </SelectTrigger>
                      <SelectContent>
                        {songOptions.hadroh.pilihan.map((song) => (
                          <SelectItem key={song} value={song}>{song}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <>
                      <Input
                        placeholder="Masukkan judul lagu bebas (contoh: Kun Anta - Humood)"
                        value={formData.laguPilihan}
                        onChange={(e) => updateFormData('laguPilihan', e.target.value)}
                        className={errors.laguPilihan ? 'border-red-500' : ''}
                      />
                      <p className="text-gray-500 text-xs">Untuk Nasyid, silakan tuliskan judul lagu bebas yang akan dibawakan.</p>
                    </>
                  )}
                  {errors.laguPilihan && <p className="text-red-500 text-sm">{errors.laguPilihan}</p>}
                </div>

                {/* Info Section */}
                <div className="bg-gray-50 rounded-xl p-4 mt-4">
                  <h4 className="font-semibold text-[#1a4d2e] mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Daftar Lagu {formData.jenisLomba === 'hadroh' ? 'Hadroh' : 'Nasyid'}
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-1">Lagu Wajib:</p>
                      <ul className="space-y-1">
                        {songOptions[formData.jenisLomba]?.wajib.map((song, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-5 h-5 bg-[#1a4d2e] text-white rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                            <span className={formData.laguWajib === song ? 'font-semibold text-[#1a4d2e]' : ''}>{song}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Pilihan Lagu:</p>
                      <ul className="space-y-1">
                        {formData.jenisLomba === 'hadroh' ? (
                          songOptions.hadroh.pilihan.map((song, idx) => (
                            <li key={idx} className="flex items-center gap-2">
                              <span className="w-5 h-5 bg-[#c9a227] text-white rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                              <span className={formData.laguPilihan === song ? 'font-semibold text-[#c9a227]' : ''}>{song}</span>
                            </li>
                          ))
                        ) : (
                          <li className="text-gray-600 italic">Bebas pilih lagu sesuai kreativitas grup</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
              <User className="w-5 h-5" />
              Nama Ketua/Official
            </h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="namaKetua">Nama Ketua Grup *</Label>
                <Input
                  id="namaKetua"
                  placeholder="Masukkan nama ketua"
                  value={formData.namaKetua}
                  onChange={(e) => updateFormData('namaKetua', e.target.value)}
                  className={errors.namaKetua ? 'border-red-500' : ''}
                />
                {errors.namaKetua && <p className="text-red-500 text-sm">{errors.namaKetua}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="noWhatsapp">Nomor WhatsApp Ketua *</Label>
                <Input
                  id="noWhatsapp"
                  placeholder="Contoh: 081234567890"
                  value={formData.noWhatsapp}
                  onChange={(e) => updateFormData('noWhatsapp', e.target.value)}
                  className={errors.noWhatsapp ? 'border-red-500' : ''}
                />
                {errors.noWhatsapp && <p className="text-red-500 text-sm">{errors.noWhatsapp}</p>}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="email">Email (Opsional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Masukkan email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" />
              Pre-Order Merchandise (Opsional)
            </h3>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
              <p className="font-semibold mb-1">📢 Info Penting:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Harga promo <strong>Rp 115.000</strong> hanya berlaku untuk pre-order</li>
                <li>Harga saat event nanti akan menjadi <strong>Rp 155.000</strong></li>
                <li>Kaos event tersedia untuk dibeli langsung saat event berlangsung</li>
              </ul>
            </div>

            {/* Jersey Design 1 */}
            <div className="border border-gray-200 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <Checkbox
                  id="pesanJersey1"
                  checked={formData.pesanJersey === 'design1'}
                  onCheckedChange={(checked) => {
                    updateFormData('pesanJersey', checked ? 'design1' : '');
                    if (checked && formData.jerseyDetails.length === 0) {
                      addMerchandiseDetail('jersey');
                    }
                  }}
                />
                <div className="flex-1">
                  <Label htmlFor="pesanJersey1" className="font-semibold text-lg cursor-pointer flex items-center gap-2">
                    Jersey Design 1 - Putih Hijau
                    <span className="text-xs bg-[#c9a227] text-white px-2 py-1 rounded-full">Pre Order Rp 115.000</span>
                  </Label>
                  <p className="text-gray-500 text-sm">Desain minimalis dengan accent warna hijau forest</p>
                </div>
              </div>
              
              {/* Preview Image - Clickable */}
              <div 
                className="aspect-video bg-gray-100 rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity mb-4"
                onClick={() => {
                  setPreviewType('jersey1');
                  setShowPreview(true);
                }}
              >
                <img 
                  src="/jersey-design1.jpg" 
                  alt="Jersey Design 1 Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = '/jersey-mockup.jpg'; }}
                />
              </div>
              
              {formData.pesanJersey === 'design1' && (
                <div className="space-y-3">
                  {formData.jerseyDetails.map((detail, index) => (
                    <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
                      <Select
                        value={detail.ukuran}
                        onValueChange={(value) => updateMerchandiseDetail('jersey', index, 'ukuran', value)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ukuranOptions.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={detail.jumlah}
                        onChange={(e) => updateMerchandiseDetail('jersey', index, 'jumlah', e.target.value)}
                        className="w-20"
                        min="1"
                      />
                      <span className="text-gray-500">pcs</span>
                      {formData.jerseyDetails.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMerchandiseDetail('jersey', index)}
                          className="text-red-500 ml-auto"
                        >
                          Hapus
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addMerchandiseDetail('jersey')}
                    className="w-full"
                  >
                    + Tambah Ukuran Lain
                  </Button>
                </div>
              )}
            </div>

            {/* Jersey Design 2 */}
            <div className="border border-gray-200 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <Checkbox
                  id="pesanJersey2"
                  checked={formData.pesanJersey === 'design2'}
                  onCheckedChange={(checked) => {
                    updateFormData('pesanJersey', checked ? 'design2' : '');
                    if (checked && formData.jerseyDetails.length === 0) {
                      addMerchandiseDetail('jersey');
                    }
                  }}
                />
                <div className="flex-1">
                  <Label htmlFor="pesanJersey2" className="font-semibold text-lg cursor-pointer flex items-center gap-2">
                    Jersey Design 2 - Hitam Gold
                    <span className="text-xs bg-[#c9a227] text-white px-2 py-1 rounded-full">Pre Order Rp 115.000</span>
                  </Label>
                  <p className="text-gray-500 text-sm">Desain elegan dengan accent warna gold</p>
                </div>
              </div>
              
              {/* Preview Image - Clickable */}
              <div 
                className="aspect-video bg-gray-100 rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity mb-4"
                onClick={() => {
                  setPreviewType('jersey2');
                  setShowPreview(true);
                }}
              >
                <img 
                  src="/jersey-design2.jpg" 
                  alt="Jersey Design 2 Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = '/jersey-mockup.jpg'; }}
                />
              </div>
              
              {formData.pesanJersey === 'design2' && (
                <div className="space-y-3">
                  {formData.jerseyDetails.map((detail, index) => (
                    <div key={index} className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
                      <Select
                        value={detail.ukuran}
                        onValueChange={(value) => updateMerchandiseDetail('jersey', index, 'ukuran', value)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ukuranOptions.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={detail.jumlah}
                        onChange={(e) => updateMerchandiseDetail('jersey', index, 'jumlah', e.target.value)}
                        className="w-20"
                        min="1"
                      />
                      <span className="text-gray-500">pcs</span>
                      {formData.jerseyDetails.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMerchandiseDetail('jersey', index)}
                          className="text-red-500 ml-auto"
                        >
                          Hapus
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addMerchandiseDetail('jersey')}
                    className="w-full"
                  >
                    + Tambah Ukuran Lain
                  </Button>
                </div>
              )}
            </div>

            {/* Topi Event */}
            <div className="border border-gray-200 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-4">
                <Checkbox
                  id="pesanTopi"
                  checked={formData.pesanTopi}
                  onCheckedChange={(checked) => {
                    updateFormData('pesanTopi', checked);
                    if (checked && !formData.topiJumlah) {
                      updateFormData('topiJumlah', '1');
                    }
                  }}
                />
                <div className="flex-1">
                  <Label htmlFor="pesanTopi" className="font-semibold text-lg cursor-pointer flex items-center gap-2">
                    Topi Event IC Harmony
                    <span className="text-xs bg-[#c9a227] text-white px-2 py-1 rounded-full">Pre Order Rp 115.000</span>
                  </Label>
                  <p className="text-gray-500 text-sm">Topi premium dengan embroidery logo IC Harmony</p>
                </div>
              </div>
              
              {/* Preview Image - Clickable */}
              <div 
                className="aspect-video bg-gray-100 rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity mb-4"
                onClick={() => {
                  setPreviewType('topi');
                  setShowPreview(true);
                }}
              >
                <img 
                  src="/topi-mockup.jpg" 
                  alt="Topi Event Preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = '/placeholder-topi.jpg'; }}
                />
              </div>
              
              {formData.pesanTopi && (
                <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl">
                  <span className="text-gray-600">Jumlah:</span>
                  <Input
                    type="number"
                    value={formData.topiJumlah}
                    onChange={(e) => updateFormData('topiJumlah', e.target.value)}
                    className="w-20"
                    min="1"
                  />
                  <span className="text-gray-500">pcs</span>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
              <Check className="w-5 h-5" />
              Ringkasan & Konfirmasi
            </h3>
            
            <div className="bg-gray-50 rounded-2xl p-6 space-y-4">
              <div>
                <h4 className="font-semibold text-[#1a4d2e] mb-2">Data Pendaftaran</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nama Grup:</span>
                    <p className="font-medium">{formData.namaGrup}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Jenis Lomba:</span>
                    <p className="font-medium capitalize">{formData.jenisLomba === 'hadroh' ? 'Festival Hadroh' : 'Festival Nasyid'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Asal Instansi:</span>
                    <p className="font-medium">{formData.asalInstansi}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Jumlah Anggota:</span>
                    <p className="font-medium">{formData.jumlahAnggota} orang</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Nama Ketua:</span>
                    <p className="font-medium">{formData.namaKetua}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">WhatsApp:</span>
                    <p className="font-medium">{formData.noWhatsapp}</p>
                  </div>
                </div>
              </div>

              {/* Pilihan Lagu */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#1a4d2e] mb-3 flex items-center gap-2">
                  <Music className="w-4 h-4 text-[#c9a227]" />
                  Pilihan Lagu
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm bg-blue-50 p-4 rounded-xl">
                  <div>
                    <span className="text-gray-500">Lagu Wajib:</span>
                    <p className="font-medium text-[#1a4d2e]">{formData.laguWajib || '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Lagu Pilihan:</span>
                    <p className="font-medium text-[#1a4d2e]">{formData.laguPilihan || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Ringkasan Biaya */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#1a4d2e] mb-3 flex items-center gap-2">
                  <span className="text-[#c9a227]">💰</span> Ringkasan Biaya
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Biaya Pendaftaran ({formData.jenisLomba === 'hadroh' ? 'Hadroh' : 'Nasyid'}):</span>
                    <span className="font-bold text-[#1a4d2e]">{formData.jenisLomba === 'hadroh' ? 'Rp 150.000' : 'Rp 75.000'}</span>
                  </div>
                  {formData.pesanJersey && formData.jerseyDetails.length > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        Jersey {formData.pesanJersey === 'design1' ? 'Design 1' : 'Design 2'} (Pre Order):
                      </span>
                      <div className="text-right">
                        <span className="font-bold text-[#1a4d2e]">
                          Rp {(formData.jerseyDetails.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) * 115000).toLocaleString('id-ID')}
                        </span>
                        <p className="text-xs text-green-600">Promo: Rp 115.000/pcs (dari Rp 155.000)</p>
                      </div>
                    </div>
                  )}
                  {formData.pesanTopi && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Topi Event (Pre Order):</span>
                      <div className="text-right">
                        <span className="font-bold text-[#1a4d2e]">
                          Rp {(parseInt(formData.topiJumlah || 0) * 115000).toLocaleString('id-ID')}
                        </span>
                        <p className="text-xs text-green-600">Promo: Rp 115.000/pcs (dari Rp 155.000)</p>
                      </div>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2 flex justify-between items-center">
                    <span className="font-semibold text-[#1a4d2e]">Total Pembayaran:</span>
                    <span className="font-bold text-lg text-[#c9a227]">
                      Rp {((formData.jenisLomba === 'hadroh' ? 150000 : 75000) + 
                        (formData.pesanJersey ? formData.jerseyDetails.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) * 115000 : 0) +
                        (formData.pesanTopi ? (parseInt(formData.topiJumlah) || 0) * 115000 : 0)
                      ).toLocaleString('id-ID')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Info Transfer Bank */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-[#1a4d2e] mb-3 flex items-center gap-2">
                  <span className="text-[#c9a227]">🏦</span> Info Transfer Bank
                </h4>
                <div className="bg-[#1a4d2e]/5 p-4 rounded-xl space-y-2 text-sm">
                  <p className="font-medium text-[#1a4d2e]">Bank BRI</p>
                  <p className="text-gray-600">No. Rekening: <span className="font-mono font-bold text-[#1a4d2e]">6102-0102-8075-538</span></p>
                  <p className="text-gray-600">Atas Nama: <span className="font-medium">FATHUR ROSI</span></p>
                  <p className="text-xs text-gray-500 mt-2">* Silakan transfer sesuai nominal dan upload bukti di grup WA</p>
                </div>
              </div>

              {(formData.pesanJersey || formData.pesanTopi) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-[#1a4d2e] mb-2">Pesanan Pre-Order</h4>
                  {formData.pesanJersey && formData.jerseyDetails.length > 0 && (
                    <div className="mb-2">
                      <span className="text-gray-500 text-sm">
                        Jersey {formData.pesanJersey === 'design1' ? 'Design 1 (Putih Hijau)' : 'Design 2 (Hitam Gold)'}:
                      </span>
                      <ul className="text-sm">
                        {formData.jerseyDetails.map((detail, idx) => (
                          <li key={idx} className="font-medium">
                            Ukuran {detail.ukuran}: {detail.jumlah} pcs
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {formData.pesanTopi && (
                    <div>
                      <span className="text-gray-500 text-sm">Topi Event:</span>
                      <p className="text-sm font-medium">{formData.topiJumlah || 0} pcs</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="setuju"
                checked={formData.setuju}
                onCheckedChange={(checked) => updateFormData('setuju', checked)}
              />
              <div>
                <Label htmlFor="setuju" className="cursor-pointer">
                  Saya menyatakan data yang diisi sudah benar dan bersedia mengikuti seluruh ketentuan lomba IC Harmony 2026.
                </Label>
                {errors.setuju && <p className="text-red-500 text-sm mt-1">{errors.setuju}</p>}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section id="daftar" className="py-20 md:py-32 bg-gradient-to-b from-white to-[#f8faf8]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-[#c9a227] font-semibold text-sm uppercase tracking-wider mb-3">
            Pendaftaran Online
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a4d2e] mb-6">
            Daftar Sekarang
          </h2>
          <div className="w-24 h-1 bg-[#c9a227] mx-auto rounded-full mb-6" />
          <p className="max-w-2xl mx-auto text-gray-600">
            Isi formulir pendaftaran berikut untuk mendaftarkan grup Anda dalam IC Harmony 2026
          </p>
        </div>

        {/* Progress Steps - Hide step 4 (Merch) */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 5].map((s, index) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                  s <= step 
                    ? 'bg-[#1a4d2e] text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {s < step ? <Check className="w-5 h-5" /> : (s === 5 ? 4 : s)}
                </div>
                {index < 3 && (
                  <div className={`w-full h-1 mx-2 ${
                    s < step ? 'bg-[#1a4d2e]' : 'bg-gray-200'
                  }`} style={{ width: '40px' }} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>Data Umum</span>
            <span>Pilihan Lagu</span>
            <span>Ketua</span>
            <span>Konfirmasi</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
            {renderStep()}

            {/* Error Display */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-red-600 text-sm font-medium">{submitError}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="border-[#1a4d2e] text-[#1a4d2e]"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Kembali
                </Button>
              ) : (
                <div />
              )}
              
              {step < 5 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={isSubmitting}
                  className="bg-[#1a4d2e] hover:bg-[#2d6a4f]"
                >
                  Lanjut
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#c9a227] hover:bg-[#b8941f] text-[#1a4d2e] font-bold"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Kirim Pendaftaran
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#1a4d2e]">
                {previewType === 'jersey1' ? 'Jersey Design 1 - Putih Hijau' : 
                 previewType === 'jersey2' ? 'Jersey Design 2 - Hitam Gold' :
                 previewType === 'topi' ? 'Topi Event IC Harmony' :
                 'Preview Merchandise'}
              </DialogTitle>
            </DialogHeader>
            <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
              <img 
                src={
                  previewType === 'jersey1' ? '/jersey-design1.jpg' :
                  previewType === 'jersey2' ? '/jersey-design2.jpg' :
                  previewType === 'topi' ? '/topi-mockup.jpg' :
                  '/jersey-mockup.jpg'
                }
                alt={`Preview ${previewType}`}
                className="w-full h-full object-contain"
                onError={(e) => { e.target.src = '/placeholder-merch.jpg'; }}
              />
            </div>
            <p className="text-center text-gray-600 text-sm">
              {previewType === 'jersey1' ? 'Desain minimalis dengan accent warna hijau forest' :
               previewType === 'jersey2' ? 'Desain elegan dengan accent warna gold' :
               previewType === 'topi' ? 'Topi premium dengan embroidery logo IC Harmony' :
               'Desain eksklusif IC Harmony 2026'}
            </p>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

// ==================== FAQ SECTION ====================
const faqs = [
  {
    question: 'Siapa saja yang boleh mendaftar IC Harmony 2026?',
    answer: 'IC Harmony 2026 terbuka untuk pelajar tingkat SMP/MTs Se-derajat, SMA/MA Se-derajat, Pondok Pesantren, Perguruan Tinggi, Lembaga Yayasan, Lembaga Masjid, dan Umum dengan usia maksimal 30 tahun.'
  },
  {
    question: 'Berapa jumlah anggota minimal dan maksimal?',
    answer: 'Untuk lomba Hadroh: minimal 10 orang dan maksimal 15 orang. Untuk lomba Nasyid: minimal 3 orang dan maksimal 6 orang.'
  },
  {
    question: 'Apakah boleh mengikuti kedua kategori lomba?',
    answer: 'Ya, satu grup boleh mengikuti kedua kategori lomba (Hadroh dan Nasyid) asalkan memenuhi persyaratan jumlah anggota masing-masing kategori.'
  },
  {
    question: 'Apakah boleh campur gender dalam satu grup?',
    answer: 'Tidak, satu grup tidak diperkenankan terdiri atas 2 gender (campuran). Grup harus bersifat ikhwan (laki-laki) atau akhwat (perempuan).'
  },
  {
    question: 'Bagaimana cara pembayaran biaya pendaftaran?',
    answer: 'Informasi mengenai biaya pendaftaran dan cara pembayaran akan diinformasikan melalui grup WhatsApp setelah Anda melakukan pendaftaran online.'
  },
  {
    question: 'Apakah merchandise wajib dibeli?',
    answer: 'Tidak, pembelian merchandise bersifat opsional. Anda dapat memesan kaos atau jersey event saat mengisi formulir pendaftaran.'
  },
  {
    question: 'Kapan dan di mana pelaksanaan lomba?',
    answer: 'Pelaksanaan lomba Hadroh tanggal 16-17 April 2026 dan lomba Nasyid tanggal 18 April 2026. Lokasi pelaksanaan akan diinformasikan pada Technical Meeting.'
  }
];

function FAQ() {
  return (
    <section id="faq" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-[#c9a227] font-semibold text-sm uppercase tracking-wider mb-3">
            <HelpCircle className="w-4 h-4" />
            FAQ
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a4d2e] mb-6">
            Pertanyaan yang Sering Diajukan
          </h2>
          <div className="w-24 h-1 bg-[#c9a227] mx-auto rounded-full mb-6" />
          <p className="max-w-2xl mx-auto text-gray-600">
            Temukan jawaban atas pertanyaan umum seputar IC Harmony 2026
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-[#f8faf8] rounded-2xl border border-[#1a4d2e]/10 px-6 data-[state=open]:bg-white data-[state=open]:shadow-lg transition-all"
              >
                <AccordionTrigger className="text-left font-semibold text-[#1a4d2e] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">
            Masih punya pertanyaan lain?
          </p>
          <a 
            href="https://wa.me/6285210488871?text=Assalamualaikum%2C%20saya%20ingin%20bertanya%20tentang%20IC%20Harmony%202026%20-%20Festival%20Hadroh%20%26%20Nasyid." 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#1a4d2e] text-white font-semibold px-8 py-3 rounded-full hover:bg-[#2d6a4f] transition-colors"
          >
            Hubungi Panitia
          </a>
        </div>
      </div>
    </section>
  );
}

// ==================== SUCCESS PAGE ====================
const waGroupLink = 'https://chat.whatsapp.com/GrdcRjXxAM34QP3330MVAI?mode=gi_t';

function SuccessPage({ formData, onReset }) {
  const [countdown, setCountdown] = useState(5);
  const [redirected, setRedirected] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!redirected) {
            setRedirected(true);
            window.open(waGroupLink, '_blank');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [formData.jenisLomba, redirected]);

  const handleManualRedirect = () => {
    window.open(waGroupLink, '_blank');
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a4d2e] via-[#2d6a4f] to-[#1a4d2e] relative overflow-hidden py-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#c9a227] rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#c9a227] rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {/* Success Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center">
            {/* Success Icon */}
            <div className="mb-6 flex justify-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#1a4d2e] mb-4">
              Pendaftaran Berhasil!
            </h1>
            
            <p className="text-gray-600 mb-8">
              Terima kasih <strong>{formData.namaGrup}</strong> telah mendaftar dalam 
              <strong> IC Harmony 2026</strong> untuk kategori{' '}
              <strong className="text-[#c9a227]">
                {formData.jenisLomba === 'hadroh' ? 'Festival Hadroh' : 'Festival Nasyid'}
              </strong>.
            </p>

            {/* Contact Admin Info */}
            <div className="mt-8 p-4 bg-[#1a4d2e]/5 rounded-2xl">
              <p className="text-[#1a4d2e] font-semibold mb-3 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Narahubung:
              </p>
              <div className="space-y-2 text-sm">
                <a href="https://wa.me/6285210488871" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#1a4d2e] hover:text-[#c9a227] transition-colors">
                  <span className="font-medium">0852-1048-8871</span>
                  <span className="text-gray-500">(Anwar)</span>
                </a>
                <a href="https://wa.me/6281290538900" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#1a4d2e] hover:text-[#c9a227] transition-colors">
                  <span className="font-medium">0812-9053-8900</span>
                  <span className="text-gray-500">(Ridho)</span>
                </a>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-[#f8faf8] rounded-2xl p-6 mb-8 text-left">
              <h3 className="font-bold text-[#1a4d2e] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Informasi Technical Meeting
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#c9a227]" />
                  <span><strong>Tanggal:</strong> 11 April 2026</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-[#c9a227]" />
                  <span><strong>Waktu:</strong> 10.00 WIB</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-[#c9a227]" />
                  <span><strong>Kegiatan:</strong> Pengambilan nomor urut & Technical Meeting</span>
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                <p className="text-yellow-800 text-sm">
                  <strong>Catatan:</strong> Kehadiran ketua grup wajib pada Technical Meeting.
                  Jika tidak hadir, grup akan didiskualifikasi.
                </p>
              </div>
            </div>

            {/* WhatsApp Redirect */}
            <div className="bg-[#1a4d2e]/5 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <MessageCircle className="w-6 h-6 text-green-600" />
                <h3 className="font-bold text-[#1a4d2e]">Gabung Grup WhatsApp</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Anda akan diarahkan ke grup WhatsApp resmi IC Harmony 2026 dalam:
              </p>
              <div className="text-4xl font-bold text-[#c9a227] mb-4">
                {countdown}
              </div>
              <Button
                onClick={handleManualRedirect}
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4 rounded-full"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Gabung Grup WhatsApp Sekarang
              </Button>
            </div>

            {/* Back Button */}
            <Button
              variant="outline"
              onClick={onReset}
              className="border-[#1a4d2e] text-[#1a4d2e] hover:bg-[#1a4d2e] hover:text-white"
            >
              Kembali ke Halaman Utama
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ==================== MAIN ICHARMONY COMPONENT ====================
export default function ICHarmony() {
  const [isRegistered, setIsRegistered] = useState(false);
  const [registeredData, setRegisteredData] = useState(null);

  const handleSubmitSuccess = (data) => {
    setRegisteredData(data);
    setIsRegistered(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setIsRegistered(false);
    setRegisteredData(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isRegistered && registeredData) {
    return (
      <>
        <MetaTags
          title="Pendaftaran Berhasil - IC Harmony 2026"
          description="Pendaftaran IC Harmony 2026 berhasil! Festival Hadroh & Nasyid Bahrul Ulum IC."
        />
        <Header />
        <SuccessPage 
          formData={{
            namaGrup: registeredData.namaGrup,
            jenisLomba: registeredData.jenisLomba,
            namaKetua: registeredData.namaKetua
          }} 
          onReset={handleReset} 
        />
        <WhatsAppFloat />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white icharmony-page">
      <MetaTags
        title="IC Harmony 2026 - Festival Hadroh & Nasyid"
        description="IC Harmony 2026 - Festival Lomba Hadroh & Nasyid oleh Bahrul Ulum Islamic Center. Daftar sekarang dan jadilah bagian dari event spektakuler ini!"
      />
      <Header />
      <main>
        <Hero />
        <About />
        <Timeline />
        <Categories />
        <Merchandise />
        <RegistrationForm onSubmitSuccess={handleSubmitSuccess} />
        <FAQ />
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
